import { useEffect, useRef, useState } from "react";
import {
  useStripeTerminalContext,
  useHoldReaderLock,
  AcquireCancel,
  ReaderAcquireFailure,
  ResolvedReader,
  readerFailureMessage,
  reportTerminalError,
} from "@/app/_providers/StripeTerminalProvider";
import { isPaymentIntentOnJobs } from "@/app/_firebase/jobs";
import { JobClient } from "../@types/job";
import useFeatureFlag from "@/app/_hooks/useFeatureFlag";
import { authenticatedFetch } from "../_utils/authenticatedFetch";
import { useUserContext } from "./useUserContext";

// Longest a charged payment waits for the reader to be let go before the
// job save runs.
const RELEASE_WAIT_MS = 3_000;

// After a card is charged for a job, check the payment actually landed on
// the job, and email contact@ if it did not: the card is charged and the job
// says nothing, which nobody would otherwise notice. Two minutes covers a
// slow save (QuickBooks, a new job being created). Runs while the page stays
// open; a closed tab is not caught here.
const RECORD_CHECK_DELAY_MS = 2 * 60_000;
const watchChargeRecorded = (args: {
  userId: string;
  paymentIntentId: string | null;
  chargeId: string | null;
  jobUids: string[];
  jobNum: number;
  amount: number;
  processingFee: number;
  chargedSince: Date;
}) => {
  if (!args.paymentIntentId) return;
  setTimeout(async () => {
    let checkError: string | undefined;
    try {
      const recorded = await isPaymentIntentOnJobs(
        args.userId,
        args.paymentIntentId,
        args.jobUids,
        args.chargedSince
      );
      if (recorded) return;
    } catch (err: any) {
      checkError = err?.message ?? String(err);
    }
    reportTerminalError(
      {
        userId: args.userId,
        stage: "charged-not-recorded",
        errorMessage: checkError
          ? `Card charged, could not check the job: ${checkError}`
          : "Card charged, but no payment with this PaymentIntent is on the job two minutes later.",
        details: {
          paymentIntentId: args.paymentIntentId,
          chargeId: args.chargeId,
          jobUids: args.jobUids,
          jobNum: args.jobNum || null,
          amount: args.amount,
          processingFee: args.processingFee,
          chargedSince: args.chargedSince.toISOString(),
        },
      },
      { always: true }
    );
  }, RECORD_CHECK_DELAY_MS);
};

export const STRIPE_CARD_DECLINE_MESSAGES: Record<string, string> = {
  authentication_required:
    "The card requires additional authentication. Please try again or use another payment method.",
  authentication_not_handled:
    "Authentication for the card was not handled. Please retry with authentication.",
  approve_with_id:
    "The payment could not be authorized. Please try again or contact the card issuer.",
  call_issuer:
    "The card was declined for an unknown reason. Please contact the card issuer.",
  card_not_supported:
    "This card does not support this type of purchase. Please use a different card.",
  card_velocity_exceeded:
    "The card’s transaction or credit limit has been exceeded. Please use another payment method.",
  currency_not_supported:
    "This card does not support the selected currency. Please try a different card or currency.",
  do_not_honor:
    "The card was declined by the issuer. Please use another payment method.",
  duplicate_transaction:
    "A similar transaction was processed very recently. Please check your recent purchases and try again if needed.",
  expired_card: "The card has expired. Please use a different card.",
  fraudulent:
    "The payment was flagged as potentially fraudulent. Please use a different payment method.",
  generic_decline:
    "The card was declined. Please try another card or contact the card issuer.",
  incorrect_address:
    "The billing address is incorrect. Please check and try again.",
  incorrect_cvc:
    "The CVC code is incorrect. Please check the security code and try again.",
  incorrect_number:
    "The card number is incorrect. Please check the number and try again.",
  incorrect_pin:
    "The PIN was incorrect. Please try again with the correct PIN.",
  incorrect_zip:
    "The billing postal code is incorrect. Please check and try again.",
  insufficient_funds:
    "The card has insufficient funds. Please use a different payment method.",
  invalid_account:
    "The card or account is invalid. Please contact the card issuer or use a different card.",
  invalid_amount:
    "The payment amount is invalid or exceeds what’s allowed. Please try again or use a different payment method.",
  invalid_cvc: "The CVC number is incorrect. Please check and try again.",
  invalid_expiry_month:
    "The expiration month is invalid. Please check and try again.",
  invalid_expiry_year:
    "The expiration year is invalid. Please check and try again.",
  invalid_number: "The card number is invalid. Please check and try again.",
  invalid_pin:
    "The PIN entered is invalid. Please try again with the correct PIN.",
  issuer_not_available:
    "The card issuer couldn’t be reached. Please try again shortly or use a different payment method.",
  lost_card:
    "The card has been reported lost. Please use a different payment method.",
  merchant_blacklist:
    "The transaction was declined. Please use a different card or contact the card issuer.",
  new_account_information_available:
    "The card or account information is no longer valid. Please use a different payment method.",
  no_action_taken:
    "The payment was declined for an unknown reason. Please use a different payment method.",
  not_permitted:
    "This payment is not permitted. Please contact the card issuer for more information.",
  offline_pin_required:
    "This card requires an offline PIN. Please insert the card and enter the PIN if supported.",
  online_or_offline_pin_required:
    "This card requires a PIN. Please insert the card and enter the PIN or use another payment method.",
  pickup_card:
    "The card cannot be used for this payment (it may be reported lost or stolen). Please use a different payment method.",
  pin_try_exceeded:
    "The PIN attempts were exceeded. Please use a different card or payment method.",
  processing_error:
    "There was an error processing the card. Please try again or use a different payment method.",
  reenter_transaction:
    "The payment could not be processed. Please try again or contact the card issuer.",
  restricted_card:
    "This card is restricted and cannot be used for this payment. Please use a different payment method.",
  revocation_of_all_authorizations:
    "The card was declined. Please use a different payment method or contact the card issuer.",
  revocation_of_authorization:
    "The card was declined. Please use a different payment method or contact the card issuer.",
  security_violation:
    "The payment could not be completed. Please try again or use a different payment method.",
  service_not_allowed:
    "This type of transaction is not allowed on this card. Please use a different payment method.",
  stolen_card:
    "The card has been reported stolen. Please use a different payment method.",
  stop_payment_order:
    "The payment was declined. Please contact the card issuer or use a different payment method.",
  testmode_decline:
    "A test card was used in live mode. Please use a real card.",
  transaction_not_allowed:
    "The card cannot be used to make this payment. Please use a different payment method.",
  withdrawal_count_limit_exceeded:
    "The card has exceeded withdrawal or transaction limits. Please use a different payment method.",
};

interface TerminalPaymentOptions {
  jobUid: string;
  jobNum: number;
  /** Absent only on a split whose payer is not one of the jobs' clients. */
  client?: JobClient | null;
  amount: number;
  processingFee: number;
  outOfBandPayment?: boolean;
  // Split payments: one charge covering several jobs. `client` is the payer,
  // who may not be any of those jobs' clients, so currency must be passed
  // explicitly rather than read off them.
  isSplit?: boolean;
  splitGroupId?: string;
  splitJobCount?: number;
  currency?: string;
  // Jobs the payment must end up on, for the charged-not-recorded check.
  // Defaults to jobUid; a split passes every job it pays.
  recordOnJobUids?: string[];
}

// serial targets one reader (the Settings page tests each row); otherwise
// the device's resolved reader is used. The busy guard is always on: there is
// no way to take a reader from a device that is mid card read.
export interface TerminalPaymentControl {
  serial?: string;
}

// Errors that came back from the reader (collect or process), as opposed to
// our own API. Tagged so they can be shown as fixed copy instead of SDK text.
export const fromReader = (error: any) =>
  error && typeof error === "object"
    ? { ...error, message: error.message, fromReader: true }
    : { message: String(error), fromReader: true };

const READER_CHARGE_FAILED =
  "The card reader could not finish the payment. Check the reader is on and on the same WiFi as this device, then try again.";
const CARD_DECLINED_GENERIC =
  "The card was declined. Try another card or another payment method.";

// One place that turns a charge failure into words for staff. Card problems
// come first and always name the reason (expired, wrong PIN, insufficient
// funds...) so staff can tell the customer. A cancelled card read says nothing
// was charged, other reader failures get fixed copy, and our own API's
// message is shown as is.
export const describeTerminalChargeError = (
  error: any
): { message: string; cardDeclined: boolean; cancelled: boolean } => {
  const code = String(error?.code || "");
  const declineCode = String(error?.decline_code || "");
  const known =
    STRIPE_CARD_DECLINE_MESSAGES[declineCode] ||
    STRIPE_CARD_DECLINE_MESSAGES[code];
  if (known) {
    return { message: known, cardDeclined: true, cancelled: false };
  }
  // A card problem with no entry in the list: Stripe's own wording is
  // written for the customer ("Your card's security code is incorrect."), so
  // it beats a generic line.
  if (code === "card_declined" || declineCode || error?.type === "card_error") {
    const stripeWords =
      typeof error?.message === "string" && error.message.trim()
        ? error.message
        : "";
    return {
      message: stripeWords || CARD_DECLINED_GENERIC,
      cardDeclined: true,
      cancelled: false,
    };
  }
  if (
    error?.cancelled ||
    /cancel/i.test(code) ||
    /cancel/i.test(String(error?.message || ""))
  ) {
    return {
      message: "Card read cancelled. Nothing was charged.",
      cardDeclined: false,
      cancelled: true,
    };
  }
  if (error?.fromReader) {
    return {
      message: READER_CHARGE_FAILED,
      cardDeclined: false,
      cancelled: false,
    };
  }
  // Our API answers { error: "..." }; a thrown Error carries message.
  const apiMessage =
    (typeof error?.error === "string" && error.error) ||
    (typeof error?.message === "string" && error.message) ||
    "The payment could not be completed. Try again.";
  return { message: apiMessage, cardDeclined: false, cancelled: false };
};

// The charge id off a processed PaymentIntent, or null. Never undefined.
//
// WHY never undefined (added 25 Oct 2026, ticket from Critters Creation
// Taxidermy, uid UtQIOrUtypOfXWboaygLnPzkLtg2):
//
// What happened: on 25 Oct 2026 (NZ time) Critters took a $350 deposit +
// $11.60 card fee on their S710 reader for client Eric Ty Cobb. Both attempts
// charged the card but the job save failed with "Transaction.set() called
// with invalid data. Unsupported field value: undefined".
//   1. 01:56 pi_3UU4V6Ge9VWlUj2x06utAtTw, during Create Job.
//      Charged, job never created.
//   2. 02:02 pi_3UU4afGe9VWlUj2x0neUaO6C, Take Payment on job #17.
//      Charged, payment never saved. The shop refunded this one in Stripe at
//      03:49 and logged a manual $350 "Other" deposit.
// The logged payment showed stripeTransactionId missing, which means it was
// undefined (null would have been logged). latest_charge came back as an
// object with no id. Firestore rejects undefined anywhere in a document, so
// the whole job write failed.
//
// Root cause never found. Ruled out: reader model and firmware (Top Pin's
// S710 on the same 2.46.4.0 saved 8 reader payments fine), Stripe side (both
// charges were clean single taps, identical to Top Pin's), the live Terminal
// SDK (Myrick, Patton's and Custom Wildlife saved reader payments hours
// later). Every other shop's reader payments in the 60 days before saved
// fine. Best guess is something in Critters' own browser (they already had a
// laptop-only reader problem with TotalAV WebShield as the suspect). The raw
// reader response was never logged, so it's unproven.
//
// Why null is safe: refunds (api/payment/refund), edit and delete all go by
// stripePaymentIntentId, not stripeTransactionId, and the Stripe webhook
// backfills the charge id onto the payment later.
//
// Removing it: don't, unless the root cause is found and fixed. Without it,
// an undefined id means the card is charged and the job is not updated, and
// nothing recovers that payment automatically. The matching `?? null` guards
// in PaymentEntryForm exist for the same reason.
//
// latest_charge lives on the PaymentIntent. The charges fallback is what
// usually resolves the id: the Terminal SDK sends its own proto shape
// (charges[], no latest_charge) regardless of the REST API version we pin,
// so keep both paths.
export const extractChargeId = (paymentIntent: any): string | null => {
  const latestCharge = paymentIntent?.latest_charge;
  if (latestCharge) {
    if (typeof latestCharge === "object") return latestCharge.id ?? null;
    if (typeof latestCharge === "string") return latestCharge;
    return null;
  }
  const charges = paymentIntent?.charges?.data;
  if (Array.isArray(charges) && charges.length > 0) {
    return charges[charges.length - 1]?.id || null;
  }
  return null;
};

export interface TerminalPaymentResult {
  success: boolean;
  stripeTransactionId: string | null;
  stripePaymentIntentId: string | null;
  error?: string;
  cardDeclined?: boolean;
  // Staff (or the customer on the reader) cancelled the card read. Nothing
  // was charged; hosts show no error for this.
  cancelled?: boolean;
  // Set when the reader could not be taken for this payment. The host shows
  // the reason inline (Process is the retry) rather than a payment error.
  readerFailure?: ReaderAcquireFailure;
  readerLabel?: string;
  // Set when the API converted the charge to the merchant's currency.
  fxRate?: number;
  processedAmount?: number;
  processedCurrency?: string;
}

// Where the reader is in a payment. "preparing" is the gap between taking
// the reader and the card screen (creating the PaymentIntent).
export type ReaderPhase =
  | "idle"
  | "connecting"
  | "preparing"
  | "collecting"
  | "processing";

// The card part of every reader charge, given to runWithReader callers:
// wait for the card, then charge it. Reader errors come back tagged
// (fromReader) so staff see fixed copy, card errors keep their codes.
export type CollectAndProcess = (clientSecret: string) => Promise<{
  paymentIntent: any;
  stripeTransactionId: string | null;
}>;

export type RunWithReaderResult<T> =
  | { ok: true; value: T }
  | {
      ok: false;
      readerFailure: ReaderAcquireFailure;
      readerLabel?: string;
      error: string;
    };

export const useTerminalPayment = () => {
  const user = useUserContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [readerPhase, setReaderPhaseState] = useState<ReaderPhase>("idle");
  const {
    terminal,
    isTerminalInitialized,
    acquireReader,
    releaseReader,
    resolvedReader,
  } = useStripeTerminalContext();

  const phaseRef = useRef<ReaderPhase>("idle");
  const unmountedRef = useRef(false);
  // Set while the reader is being taken, so Cancel can stop a slow connect.
  const cancelConnectRef = useRef<(() => void) | null>(null);
  const terminalRef = useRef(terminal);
  terminalRef.current = terminal;
  const setReaderPhase = (phase: ReaderPhase) => {
    phaseRef.current = phase;
    setReaderPhaseState(phase);
  };

  // The host closed or the form was swapped out (browser back, a different
  // job picked in POS) while a payment was under way. Mid card read: cancel
  // the collect so the reader leaves the card screen and the pending promise
  // settles. Still connecting: runWithReader sees unmountedRef and stops
  // before any card read starts. Either way the reader is released.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (phaseRef.current === "collecting") {
        terminalRef.current?.cancelCollectPaymentMethod?.().catch(() => {});
      }
    };
  }, []);

  // Payment modals stay shut from Process until the reader is let go.
  useHoldReaderLock(isProcessing);

  // Feature flag check
  const paymentsEnabled = useFeatureFlag("MM_PAYMENTS_ENABLED");

  // If payments not enabled, return stub implementation
  if (!paymentsEnabled) {
    return {
      processTerminalPayment: async (): Promise<TerminalPaymentResult> => ({
        success: false,
        stripeTransactionId: null,
        stripePaymentIntentId: null,
        error: "Payment processing not available",
      }),
      runWithReader: async <T,>(): Promise<RunWithReaderResult<T>> => ({
        ok: false,
        readerFailure: "not_initialized",
        error: "Payment processing not available",
      }),
      isProcessing: false,
      readerPhase: "idle" as ReaderPhase,
      canCancel: false,
      isTerminalReady: false,
      terminalName: null as string | null,
      resolvedReader: null as ResolvedReader | null,
      cancelTerminalPayment: async () => false,
    };
  }

  // Take the reader, run one reader interaction, let it go. Every reader flow
  // (job payment, split payment, one-off charge, Settings tests) goes through
  // here so the connect-per-payment rule has one home.
  const runWithReader = async <T,>(
    fn: (ctx: {
      terminal: NonNullable<typeof terminal>;
      setPhase: (phase: ReaderPhase) => void;
      collectAndProcess: CollectAndProcess;
    }) => Promise<T>,
    control?: TerminalPaymentControl
  ): Promise<RunWithReaderResult<T>> => {
    if (!terminal || !isTerminalInitialized) {
      return {
        ok: false,
        readerFailure: "not_initialized",
        error: readerFailureMessage("not_initialized"),
      };
    }

    setIsProcessing(true);
    setReaderPhase("connecting");
    let acquired = false;

    const ABORTED = "The payment screen was closed before the card read started. Nothing was charged.";

    try {
      // Cancel during the connect returns straight away. If the connect
      // lands afterwards the reader is let go again.
      // If the connect lands after Cancel, the provider lets the reader go
      // (or hands it to the next Process for the same reader).
      const cancel: AcquireCancel = { cancelled: false };
      const cancelled = new Promise<"cancelled">((resolve) => {
        cancelConnectRef.current = () => {
          cancel.cancelled = true;
          resolve("cancelled");
        };
      });
      const acquiring = acquireReader({
        failIfInUse: true,
        serial: control?.serial,
        cancel,
      });
      const first = await Promise.race([acquiring, cancelled]);
      cancelConnectRef.current = null;
      if (first === "cancelled") {
        throw Object.assign(new Error("Card read cancelled"), {
          code: "canceled",
          cancelled: true,
        });
      }

      const acquire = first;
      if (!acquire.reader) {
        const failure = acquire.failure ?? "error";
        return {
          ok: false,
          readerFailure: failure,
          readerLabel: acquire.readerLabel,
          error: readerFailureMessage(failure, acquire.readerLabel),
        };
      }
      acquired = true;

      if (unmountedRef.current) {
        return { ok: false, readerFailure: "error", error: ABORTED };
      }
      setReaderPhase("preparing");

      // Entering the card read is the last point a closed screen can stop
      // the payment before a card is asked for. A PaymentIntent may already
      // exist by then; it is left unused and charges nothing.
      const setPhase = (phase: ReaderPhase) => {
        if (phase === "collecting" && unmountedRef.current) {
          throw Object.assign(new Error(ABORTED), { aborted: true });
        }
        setReaderPhase(phase);
      };

      const collectAndProcess: CollectAndProcess = async (clientSecret) => {
        setPhase("collecting");
        const collectResult = await terminal.collectPaymentMethod(
          clientSecret,
          {
            config_override: {
              // @ts-ignore this is a valid config
              enable_customer_cancellation: true,
            },
          }
        );
        if ("error" in collectResult && collectResult.error) {
          throw fromReader(collectResult.error);
        }

        setPhase("processing");
        const processResult = await terminal.processPayment(
          "paymentIntent" in collectResult ? collectResult.paymentIntent : null
        );
        if ("error" in processResult && processResult.error) {
          throw fromReader(processResult.error);
        }

        const paymentIntent =
          "paymentIntent" in processResult ? processResult.paymentIntent : null;
        return {
          paymentIntent,
          stripeTransactionId: extractChargeId(paymentIntent),
        };
      };

      const value = await fn({ terminal, setPhase, collectAndProcess });
      return { ok: true, value };
    } finally {
      cancelConnectRef.current = null;
      setReaderPhase("idle");
      if (acquired) {
        // A charged card must reach the job save quickly, so letting go of
        // the reader gets a few seconds at most. If the reader has dropped
        // off the network the disconnect finishes on its own later.
        await Promise.race([
          releaseReader(),
          new Promise((resolve) => setTimeout(resolve, RELEASE_WAIT_MS)),
        ]);
      }
      setIsProcessing(false);
    }
  };

  const processTerminalPayment = async (
    userId: string,
    options: TerminalPaymentOptions,
    control?: TerminalPaymentControl
  ): Promise<TerminalPaymentResult> => {
    // A minute of slack for the "saved since the charge" check.
    const chargedSince = new Date(Date.now() - 60_000);
    try {
      const run = await runWithReader(async ({ collectAndProcess }) => {
        // Step 1: Create payment intent for the job. The reader is already
        // ours at this point, so a busy reader never leaves an orphan intent.
        const intentResponse = await authenticatedFetch(
          "/api/stripe/terminal/payment",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId,
              jobUid: options.jobUid,
              jobNum: options.jobNum,
              client: options.client,
              amount: options.amount,
              processingFee: options.processingFee,
              stripeAccountId: user.settings.stripeAccountId,
              outOfBandPayment: options.outOfBandPayment,
              merchantCurrency: user.settings.currency,
              ...(options.isSplit
                ? {
                    isSplit: true,
                    splitGroupId: options.splitGroupId,
                    splitJobCount: options.splitJobCount,
                    currency: options.currency,
                  }
                : {}),
            }),
          }
        );

        if (!intentResponse.ok) {
          const error = await intentResponse.json();
          // Surface the raw API error object so we can inspect decline codes, etc.
          throw error;
        }

        const intentData = await intentResponse.json();

        // Step 2: Wait for the card and charge it.
        const { paymentIntent, stripeTransactionId } =
          await collectAndProcess(intentData.clientSecret);

        return {
          stripeTransactionId,
          // The reader's copy of the intent should carry its id, but the
          // card is charged by now, so fall back to the id of the intent we
          // created rather than ever saving undefined (see extractChargeId).
          stripePaymentIntentId: (paymentIntent?.id ??
            intentData.paymentIntentId ??
            null) as string | null,
          fxRate: intentData.fxRate as number | undefined,
          processedAmount: intentData.processedAmount as number | undefined,
          processedCurrency: intentData.processedCurrency as string | undefined,
        };
      }, control);

      if (run.ok === false) {
        return {
          success: false,
          stripeTransactionId: null,
          stripePaymentIntentId: null,
          error: run.error,
          readerFailure: run.readerFailure,
          readerLabel: run.readerLabel,
        };
      }

      watchChargeRecorded({
        userId,
        paymentIntentId: run.value.stripePaymentIntentId,
        chargeId: run.value.stripeTransactionId,
        jobUids: options.recordOnJobUids ?? [options.jobUid],
        jobNum: options.jobNum,
        amount: options.amount,
        processingFee: options.processingFee,
        chargedSince,
      });

      return { success: true, ...run.value };
    } catch (error: any) {
      const described = describeTerminalChargeError(error);
      return {
        success: false,
        stripeTransactionId: null,
        stripePaymentIntentId: null,
        cardDeclined: described.cardDeclined,
        cancelled: described.cancelled,
        error: described.message,
      };
    }
  };

  // Stops the payment while the reader is being taken or waiting for the
  // card. Once the card is being charged it cannot be stopped.
  const cancelTerminalPayment = async () => {
    if (phaseRef.current === "connecting" && cancelConnectRef.current) {
      cancelConnectRef.current();
      return true;
    }
    if (!terminal || phaseRef.current !== "collecting") return false;

    try {
      await terminal.cancelCollectPaymentMethod();
      return true;
    } catch (error) {
      console.error("Failed to cancel payment:", error);
      return false;
    }
  };

  return {
    processTerminalPayment,
    runWithReader,
    cancelTerminalPayment,
    isProcessing,
    readerPhase,
    canCancel: readerPhase === "connecting" || readerPhase === "collecting",
    // A reader can be reached for. Nothing is connected until Process.
    isTerminalReady: isTerminalInitialized && !!resolvedReader,
    terminalName: resolvedReader?.label ?? null,
    resolvedReader,
  };
};
